import MovablePlaceholder from '../MovablePlaceholder/MovablePlaceholder';
import { Select } from 'ts/interfaces/Generic';
import { FC, useEffect, useRef, useState } from 'react';
import withErrorBoundary from 'components/ui/withErrorBoundaries';

interface DropdownMultiProps {
  options: Select[][];
  onChange: (value: string | number) => void;
  onChangeText?: (value: string) => void;
  movablePlaceholder?: string;
  movablePlaceholderIcon?: any;
  labelValue: string | number;
  isInvalid?: boolean;
  scrollOnFocusMobile?: boolean;
}

const DropdownMulti: FC<DropdownMultiProps> = (props) => {
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [hightlightedOption, setHightlightedOption] = useState(0);
  const inputRef = useRef<any>();
  const initScrollPosition = useRef(0);

  useEffect(() => {
    if (props.labelValue) {
      setInputValue(props.labelValue as string);
    }
  }, [props.labelValue]);

  const storedValue = useRef<string | number>('');

  const onClickOption = (option: Select) => {
    setInputValue(option.label);
    storedValue.current = option.label;
    props.onChange(option.value);
  };

  const onMouseDownOption = (option: Select, event: React.MouseEvent) => {
    event.preventDefault();
    setInputValue(option.label);
    storedValue.current = option.label;
    props.onChange(option.value);
    setIsFocused(false);
  };

  const onFocus = () => {
    storedValue.current = inputValue;
    setInputValue('');
    setIsFocused(true);

    if (window.innerWidth < 768 && props.scrollOnFocusMobile && inputRef.current) {
      initScrollPosition.current = window.scrollY;
      const offset = 100;
      const elementPosition = inputRef.current.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  const onBlur = () => {
    setTimeout(() => {
      setInputValue(storedValue.current as string);
      setIsFocused(false);
      storedValue.current = '';
    }, 300);

    if (window.innerWidth < 768 && props.scrollOnFocusMobile) {
      window.scrollTo({ top: initScrollPosition.current, behavior: 'smooth' });
      initScrollPosition.current = 0;
    }
  };

  const onChange = (value: string) => {
    setHightlightedOption(0);
    props.onChangeText?.(value);
    setInputValue(value);
  };

  const onKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      event.key.length === 1 &&
      (event.key.match(/[a-zA-Z]/) || event.key.match(/[\u0590-\u05FF]/))
    ) {
      setIsFocused(true);
    }
    if (event.key === 'Enter') {
      const option = props.options[0]?.[hightlightedOption];
      if (option) {
        storedValue.current = option.label;
        props.onChange(option.value);
        setInputValue(option.label);
        setIsFocused(false);
      }
    }

    if (event.key === 'ArrowDown') {
      const max = props.options[0].length - 1;
      setHightlightedOption(hightlightedOption + 1 > max ? 0 : hightlightedOption + 1);
    }

    if (event.key === 'ArrowUp') {
      setHightlightedOption(
        hightlightedOption - 1 < 0 ? props.options[0].length - 1 : hightlightedOption - 1
      );
    }

    if (event.key === 'Escape') {
      setIsFocused(false);
    }
  };
  const containsHebrew = (): boolean => {
    const hebrewPattern = /[\u0590-\u05FF]/;
    const label = props.options?.[0]?.[0].label;
    return hebrewPattern.test(label);
  };

  const inputClassName =
    'dropdown-multi-input form-control' +
    (props.movablePlaceholder ? ' movable-placeholder-input' : '') +
    (props.movablePlaceholder && !props.movablePlaceholderIcon
      ? ' movable-placeholder-input-noicon'
      : '');
  const menuClassName =
    'dropdown-multi ' +
    (props.options.length > 1 ? 'dropdown-multi-many' : 'dropdown-multi-single');

  return (
    <div className={'form-group mb-0' + (props.isInvalid ? ' is-invalid' : '')}>
      <input
        className={inputClassName}
        onFocus={onFocus}
        onBlur={onBlur}
        value={inputValue}
        onChange={(event) => onChange(event.target.value)}
        onKeyUp={onKeyPress}
        ref={inputRef}
      />
      {props.movablePlaceholder && (
        <MovablePlaceholder
          label={props.movablePlaceholder}
          showSmall={!!(inputValue || isFocused)}
          icon={props.movablePlaceholderIcon}
        />
      )}
      {isFocused && (
        <div className={menuClassName}>
          {props.options.map((select, index) => (
            <div className="dropdown-multi-item-wr" key={index}>
              <div className="dropdown-multi-item">
                {select.map((option, opIndex) => (
                  <div
                    className={
                      'dropdown-multi-option' +
                      (hightlightedOption === opIndex && index === 0 ? ' active' : '') +
                      (containsHebrew()
                        ? ' dropdown-multi-option-rtl'
                        : ' dropdown-multi-option-ltr') +
                      (option.label === props.labelValue ? ' selected' : '')
                    }
                    key={option.value}
                    onMouseDown={(event) => onMouseDownOption(option, event)}
                    onClick={() => onClickOption(option)}
                  >
                    {option.html || option.label}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default withErrorBoundary(DropdownMulti, 'DropdownMulti');
